import { OPTIMIZELY_FLAGS } from '@/constants'

interface InitialPageData {
  props?: {
    pageProps?: {
      optimizelyData?: {
        decisions?: Record<string, unknown>
        dataFile?: string
      }
    }
  }
}

interface ExperimentRule {
  id: string
  variations?: { key: string }[]
}

interface ExperimentDatafile {
  featureFlags?: { key: string; experimentIds?: string[]; rolloutId?: string }[]
  experiments?: ExperimentRule[]
  groups?: { experiments?: ExperimentRule[] }[]
  rollouts?: { id: string; experiments?: ExperimentRule[] }[]
}

// The effect-populated optimizely_decision global may not exist during hydration.
// Read the initial server assignments instead, without evaluating new decisions.
export const getInitialExperimentAttributes = (): Record<string, string> => {
  try {
    const nextData = (window as Window & { __NEXT_DATA__?: InitialPageData })
      .__NEXT_DATA__
    const optimizelyData = nextData?.props?.pageProps?.optimizelyData
    const decisions = optimizelyData?.decisions
    if (!decisions || typeof optimizelyData?.dataFile !== 'string') return {}

    const datafile: ExperimentDatafile = JSON.parse(optimizelyData.dataFile)
    const experiments = [
      ...(datafile.experiments ?? []),
      ...(datafile.groups ?? []).flatMap((group) => group.experiments ?? []),
    ]
    const attributes: Record<string, string> = {}

    for (const flag of OPTIMIZELY_FLAGS) {
      if (
        !decisions ||
        !Object.prototype.hasOwnProperty.call(decisions, flag)
      ) {
        continue
      }

      const decision = decisions[flag]
      if (typeof decision !== 'object' || decision === null) continue

      const variationKey = (decision as { variationKey?: unknown }).variationKey
      if (
        typeof variationKey !== 'string' ||
        !/^[A-Za-z0-9_-]{1,128}$/.test(variationKey)
      )
        continue

      const feature = datafile.featureFlags?.find(({ key }) => key === flag)
      if (!feature) continue

      const rules = [
        ...experiments.filter(({ id }) => feature.experimentIds?.includes(id)),
        ...(datafile.rollouts?.find(({ id }) => id === feature.rolloutId)
          ?.experiments ?? []),
      ]

      // Forced query decisions can contain arbitrary strings. Only accept keys
      // configured for this flag in the server-supplied datafile.
      if (
        rules.some((rule) =>
          rule.variations?.some(({ key }) => key === variationKey),
        )
      ) {
        attributes[`initial_experiment_${flag}_variation`] = variationKey
      }
    }

    return attributes
  } catch {
    // Experiment diagnostics must never prevent the original error being reported.
    return {}
  }
}
